import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { api, type ApiResponse } from "@/api/client";
import MediaPicker from "@/components/common/MediaPicker";
import type { NewsletterSection } from "./studioTypes";

/**
 * Per-type inline field editor for one newsletter section (Řez 6). Pure controlled component:
 * `value` in, `onChange(next)` out — the page owns the edition state, preview debouncing and
 * persistence. Narrative-heavy edits are better done via the prompt-edit box; these fields are
 * for surgical manual fixes, media attachment and trip selection.
 */

interface TripOption {
  id: string;
  title: string;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">
        {label}
      </span>
      {children}
    </label>
  );
}

function TextInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}) {
  return (
    <input
      type="text"
      value={value}
      placeholder={placeholder}
      onChange={(e) => onChange(e.target.value)}
      className="focus:ring-primary-500 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1"
    />
  );
}

function TextArea({
  value,
  onChange,
  rows = 3,
}: {
  value: string;
  onChange: (v: string) => void;
  rows?: number;
}) {
  return (
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className="focus:ring-primary-500 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-1"
    />
  );
}

/** Newline-separated list editor for string arrays (bullets, stops, included/excluded). */
function ListEditor({
  value,
  onChange,
  rows = 3,
}: {
  value: string[];
  onChange: (v: string[]) => void;
  rows?: number;
}) {
  return (
    <TextArea
      value={value.join("\n")}
      rows={rows}
      onChange={(v) =>
        onChange(
          v
            .split("\n")
            .map((s) => s.trim())
            .filter(Boolean),
        )
      }
    />
  );
}

export default function SectionFieldsEditor({
  value,
  onChange,
}: {
  value: NewsletterSection;
  onChange: (next: NewsletterSection) => void;
}) {
  const { t } = useTranslation();
  const { data: tripsData } = useQuery({
    queryKey: ["studio-trip-options"],
    queryFn: () => api.get<ApiResponse<TripOption[]>>("/api/admin/trips?fields=slim"),
    enabled: value.type === "tripCards",
    staleTime: 5 * 60_000,
  });
  // Scorer ordering (popularity + soonness + business rules) — "suggested" first in the picker.
  const { data: topData } = useQuery({
    queryKey: ["studio-trip-suggestions"],
    queryFn: () =>
      api.get<ApiResponse<Array<{ tripId: string; reasons: string[] }>>>("/api/admin/recsys/top"),
    enabled: value.type === "tripCards",
    staleTime: 5 * 60_000,
  });
  const rawOptions: TripOption[] = Array.isArray(tripsData?.data) ? tripsData!.data : [];
  const suggestedRank = new Map(
    (topData?.data ?? []).map((t, i) => [t.tripId, i] as [string, number]),
  );
  const tripOptions = [...rawOptions].sort((a, b) => {
    const ra = suggestedRank.has(a.id) ? suggestedRank.get(a.id)! : 999;
    const rb = suggestedRank.has(b.id) ? suggestedRank.get(b.id)! : 999;
    return ra - rb;
  });
  const isSuggested = (id: string) => suggestedRank.has(id) && suggestedRank.get(id)! < 8;

  const set = (patch: Record<string, unknown>) =>
    onChange({ ...value, ...patch } as NewsletterSection);

  switch (value.type) {
    case "bar":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("newsletter.sections.fields.left")}>
            <TextInput value={value.left} onChange={(v) => set({ left: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.right")}>
            <TextInput value={value.right} onChange={(v) => set({ right: v })} />
          </Field>
        </div>
      );

    case "hero":
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("newsletter.sections.fields.eyebrow")}>
              <TextInput value={value.eyebrow} onChange={(v) => set({ eyebrow: v })} />
            </Field>
            <Field label={t("newsletter.sections.fields.accent")}>
              <TextInput
                value={value.titleAccent ?? ""}
                onChange={(v) => set({ titleAccent: v })}
              />
            </Field>
          </div>
          <Field label={t("newsletter.sections.fields.title")}>
            <TextInput value={value.title} onChange={(v) => set({ title: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.subtitle")}>
            <TextInput value={value.subtitle ?? ""} onChange={(v) => set({ subtitle: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.image")}>
            <MediaPicker
              mode="single"
              value={value.mediaId ?? null}
              onChange={(id) => set({ mediaId: id })}
              folder="general"
            />
          </Field>
        </div>
      );

    case "intro":
      return (
        <div className="space-y-3">
          {/* The literal "{{firstName}}" token is inserted via interpolation so i18next
              doesn't try to resolve it as a variable of the label itself. */}
          <Field label={t("newsletter.sections.fields.greeting", { placeholder: "{{firstName}}" })}>
            <TextInput value={value.greeting ?? ""} onChange={(v) => set({ greeting: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.body")}>
            <TextArea value={value.body} rows={4} onChange={(v) => set({ body: v })} />
          </Field>
        </div>
      );

    case "trustBoxes":
      return (
        <div className="grid grid-cols-3 gap-3">
          {value.items.map((item, i) => (
            <div key={i} className="space-y-1">
              <TextInput
                value={item.label}
                placeholder={t("newsletter.sections.fields.trustLabelPlaceholder")}
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], label: v };
                  set({ items });
                }}
              />
              <TextInput
                value={item.value}
                placeholder={t("newsletter.sections.fields.trustValuePlaceholder")}
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], value: v };
                  set({ items });
                }}
              />
            </div>
          ))}
        </div>
      );

    case "spotlight":
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("newsletter.sections.fields.variant")}>
              <select
                value={value.variant}
                onChange={(e) => set({ variant: e.target.value })}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
              >
                <option value="guide">{t("newsletter.sections.fields.variantGuide")}</option>
                <option value="branch">{t("newsletter.sections.fields.variantBranch")}</option>
                <option value="custom">{t("newsletter.sections.fields.variantCustom")}</option>
              </select>
            </Field>
            <Field label={t("newsletter.sections.fields.title")}>
              <TextInput value={value.title} onChange={(v) => set({ title: v })} />
            </Field>
          </div>
          <Field label={t("newsletter.sections.fields.body")}>
            <TextArea value={value.body} onChange={(v) => set({ body: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.bullets")}>
            <ListEditor value={value.bullets ?? []} onChange={(v) => set({ bullets: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.photo")}>
            <MediaPicker
              mode="single"
              value={value.mediaId ?? null}
              onChange={(id) => set({ mediaId: id })}
              folder="general"
            />
          </Field>
        </div>
      );

    case "tripCards": {
      const selected = value.tripIds;
      return (
        <div className="space-y-3">
          <div className="flex items-center gap-4">
            <Field label={t("newsletter.sections.fields.mode")}>
              <select
                value={value.mode}
                onChange={(e) => set({ mode: e.target.value })}
                className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
              >
                <option value="personalized">
                  {t("newsletter.sections.fields.modePersonalized")}
                </option>
                <option value="manual">{t("newsletter.sections.fields.modeManual")}</option>
              </select>
            </Field>
            <label className="mt-5 flex items-center gap-2 text-sm text-gray-600">
              <input
                type="checkbox"
                checked={value.discoverySlot}
                onChange={(e) => set({ discoverySlot: e.target.checked })}
              />
              {t("newsletter.sections.fields.discoverySlot")}
            </label>
          </div>
          <Field
            label={
              value.mode === "manual"
                ? t("newsletter.sections.fields.tripsExact")
                : t("newsletter.sections.fields.tripsFallback")
            }
          >
            <div className="space-y-1.5">
              {selected.map((id, i) => {
                const trip = tripOptions.find((t) => t.id === id);
                return (
                  <div key={`${id}-${i}`} className="flex items-center gap-2">
                    <span className="flex-1 truncate rounded bg-gray-50 px-2 py-1 text-sm">
                      {trip?.title ?? id}
                    </span>
                    <input
                      type="text"
                      placeholder={t("newsletter.sections.fields.hookPlaceholder")}
                      value={value.hooks?.[id] ?? ""}
                      onChange={(e) =>
                        set({ hooks: { ...(value.hooks ?? {}), [id]: e.target.value } })
                      }
                      className="w-56 rounded-lg border border-gray-200 px-2 py-1 text-xs"
                    />
                    <button
                      type="button"
                      onClick={() => set({ tripIds: selected.filter((x) => x !== id) })}
                      className="text-xs text-red-500 hover:underline"
                    >
                      {t("common.actions.remove")}
                    </button>
                  </div>
                );
              })}
              <select
                value=""
                onChange={(e) => {
                  if (e.target.value && !selected.includes(e.target.value)) {
                    set({ tripIds: [...selected, e.target.value] });
                  }
                }}
                className="w-full rounded-lg border border-dashed border-gray-300 px-3 py-1.5 text-sm text-gray-500"
              >
                <option value="">{t("newsletter.sections.fields.addTrip")}</option>
                {tripOptions
                  .filter((t) => !selected.includes(t.id))
                  .map((t) => (
                    <option key={t.id} value={t.id}>
                      {isSuggested(t.id) ? "★ " : ""}
                      {t.title}
                    </option>
                  ))}
              </select>
            </div>
          </Field>
        </div>
      );
    }

    case "review":
      return (
        <div className="space-y-3">
          <Field label={t("newsletter.sections.fields.quote")}>
            <TextArea value={value.quote} onChange={(v) => set({ quote: v })} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("newsletter.sections.fields.author")}>
              <TextInput value={value.author} onChange={(v) => set({ author: v })} />
            </Field>
            <Field label={t("newsletter.sections.fields.context")}>
              <TextInput value={value.context} onChange={(v) => set({ context: v })} />
            </Field>
          </div>
          <Field label={t("newsletter.sections.fields.linkOptional")}>
            <TextInput
              value={value.linkUrl ?? ""}
              onChange={(v) => set({ linkUrl: v || undefined })}
            />
          </Field>
        </div>
      );

    case "included":
      return (
        <div className="grid grid-cols-2 gap-3">
          <Field label={t("newsletter.sections.fields.includedList")}>
            <ListEditor value={value.included} rows={5} onChange={(v) => set({ included: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.excludedList")}>
            <ListEditor value={value.excluded} rows={5} onChange={(v) => set({ excluded: v })} />
          </Field>
        </div>
      );

    case "video":
      return (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <Field label={t("newsletter.sections.fields.eyebrow")}>
              <TextInput value={value.eyebrow} onChange={(v) => set({ eyebrow: v })} />
            </Field>
            <Field label={t("newsletter.sections.fields.title")}>
              <TextInput value={value.title} onChange={(v) => set({ title: v })} />
            </Field>
          </div>
          <Field label={t("newsletter.sections.fields.youtubeUrl")}>
            <TextInput value={value.youtubeUrl} onChange={(v) => set({ youtubeUrl: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.caption")}>
            <TextInput value={value.caption} onChange={(v) => set({ caption: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.thumbnail")}>
            <MediaPicker
              mode="single"
              value={value.mediaId ?? null}
              onChange={(id) => set({ mediaId: id })}
              folder="general"
            />
          </Field>
        </div>
      );

    case "routeTimeline":
      return (
        <div className="space-y-3">
          <Field label={t("newsletter.sections.fields.titleTripName")}>
            <TextInput value={value.title} onChange={(v) => set({ title: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.stops")}>
            <ListEditor value={value.stops} onChange={(v) => set({ stops: v })} />
          </Field>
        </div>
      );

    case "combos":
      return (
        <div className="space-y-3">
          {value.items.map((item, i) => (
            <div key={i} className="grid grid-cols-4 gap-2 rounded-lg bg-gray-50 p-2">
              <TextInput
                value={item.title}
                placeholder={t("newsletter.sections.fields.comboTitle")}
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], title: v };
                  set({ items });
                }}
              />
              <TextInput
                value={item.subtitle}
                placeholder={t("newsletter.sections.fields.comboSubtitle")}
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], subtitle: v };
                  set({ items });
                }}
              />
              <TextInput
                value={item.price}
                placeholder={t("newsletter.sections.fields.comboPrice")}
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], price: v };
                  set({ items });
                }}
              />
              <TextInput
                value={item.linkUrl}
                placeholder="https://…"
                onChange={(v) => {
                  const items = [...value.items];
                  items[i] = { ...items[i], linkUrl: v };
                  set({ items });
                }}
              />
            </div>
          ))}
        </div>
      );

    case "note":
      return (
        <div className="space-y-3">
          <Field label={t("newsletter.sections.fields.body")}>
            <TextArea value={value.body} onChange={(v) => set({ body: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.tone")}>
            <select
              value={value.tone}
              onChange={(e) => set({ tone: e.target.value })}
              className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm"
            >
              <option value="info">{t("newsletter.sections.fields.toneInfo")}</option>
              <option value="warning">{t("newsletter.sections.fields.toneWarning")}</option>
            </select>
          </Field>
        </div>
      );

    case "helpDesk":
      return (
        <div className="space-y-3">
          <Field label={t("newsletter.sections.fields.title")}>
            <TextInput value={value.title} onChange={(v) => set({ title: v })} />
          </Field>
          <Field label={t("newsletter.sections.fields.body")}>
            <TextArea value={value.body} onChange={(v) => set({ body: v })} />
          </Field>
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("newsletter.sections.fields.ctaLabel")}>
              <TextInput value={value.ctaLabel} onChange={(v) => set({ ctaLabel: v })} />
            </Field>
            <Field label={t("newsletter.sections.fields.ctaUrl")}>
              <TextInput value={value.ctaUrl} onChange={(v) => set({ ctaUrl: v })} />
            </Field>
            <Field label={t("newsletter.sections.fields.phone")}>
              <TextInput value={value.phone} onChange={(v) => set({ phone: v })} />
            </Field>
          </div>
        </div>
      );

    case "footer":
      return <p className="text-sm text-gray-500">{t("newsletter.sections.fields.footerNote")}</p>;

    default:
      return null;
  }
}
